'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  name: string;
  business: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  date: string;
}

interface TestimonialsSectionProps {
  className?: string;
}

const TestimonialsSection = ({ className = '' }: TestimonialsSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Алия Нурсултанова',
    business: 'Владелец сети мини-маркетов "Продукты 24"',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_162145b5d-1767883813330.png",
    alt: 'Улыбающаяся казахстанская женщина в деловом костюме на фоне магазина',
    rating: 5,
    text: 'Работаем с Optovichok.kz уже более года. Отличные цены, быстрая доставка и всегда качественный товар. Особенно нравится система заказов через заявки - менеджер всегда помогает подобрать оптимальное количество.',
    date: '15.01.2026'
  },
  {
    id: '2',
    name: 'Ерлан Абдуллаев',
    business: 'Директор гостиницы "Астана Плаза"',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14212b399-1763294371684.png",
    alt: 'Казахстанский мужчина в темном костюме в офисе отеля',
    rating: 5,
    text: 'Закупаем у них всю бытовую химию и средства гигиены для гостиницы. Выгодные цены при покупке коробами, всегда есть в наличии нужные позиции. Персональный менеджер оперативно решает все вопросы.',
    date: '08.01.2026'
  },
  {
    id: '3',
    name: 'Динара Сапарова',
    business: 'Владелец магазина "Хозтовары+"',
    image: "https://images.unsplash.com/photo-1578924107684-e6815f4734b9",
    alt: 'Молодая казахстанская женщина в синей рубашке в торговом зале',
    rating: 5,
    text: 'Очень довольна сотрудничеством! Широкий ассортимент товаров, удобная система оформления заказов. Цены действительно оптовые, что позволяет держать конкурентные цены в своем магазине.',
    date: '22.12.2025'
  }];


  const handlePrevious = () => {
    setActiveIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setActiveIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  if (!isHydrated) {
    return (
      <section className={`bg-background py-12 lg:py-16 ${className}`}>
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <div className="mb-12 text-center">
            <h2 className="mb-4 font-heading text-2xl font-semibold text-text-primary lg:text-3xl">
              Отзывы наших клиентов
            </h2>
          </div>
          
          <div className="mx-auto max-w-4xl">
            <div className="overflow-hidden rounded-md bg-card p-8 shadow-sm">
              <div className="mb-6 flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-muted" />
                <div className="flex-1">
                  <div className="mb-2 h-5 w-32 rounded bg-muted" />
                  <div className="h-4 w-48 rounded bg-muted" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-3/4 rounded bg-muted" />
              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  const currentTestimonial = testimonials[activeIndex];

  return (
    <section className={`bg-background py-12 lg:py-16 ${className}`}>
      <div className="mx-auto max-w-7xl px-4 lg:px-6">
        <div className="mb-12 text-center">
          <h2 className="mb-4 font-heading text-2xl font-semibold text-text-primary lg:text-3xl">
            Отзывы наших клиентов
          </h2>
          <p className="mx-auto max-w-2xl text-base text-text-secondary lg:text-lg">
            Более 500 довольных клиентов доверяют нам свои оптовые закупки
          </p>
        </div>
        
        <div className="relative mx-auto max-w-4xl">
          <div className="overflow-hidden rounded-md bg-card p-8 shadow-sm lg:p-12">
            <div className="mb-6 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-full">
                <AppImage
                  src={currentTestimonial.image}
                  alt={currentTestimonial.alt}
                  className="h-full w-full object-cover" />

              </div>
              
              <div className="flex-1">
                <h3 className="mb-1 font-heading text-lg font-semibold text-text-primary">
                  {currentTestimonial.name}
                </h3>
                <p className="mb-2 font-caption text-sm text-text-secondary">
                  {currentTestimonial.business}
                </p>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) =>
                  <Icon
                    key={i}
                    name="StarIcon"
                    variant="solid"
                    size={16}
                    className={i < currentTestimonial.rating ? 'text-accent' : 'text-muted'} />

                  )}
                </div>
              </div>
              
              <div className="font-caption text-sm text-text-secondary">
                {currentTestimonial.date}
              </div>
            </div>
            
            <blockquote className="mb-6 text-base leading-relaxed text-text-primary lg:text-lg">
              "{currentTestimonial.text}"
            </blockquote>
            
            <div className="flex items-center justify-between border-t border-border pt-6">
              <div className="flex items-center gap-2">
                {testimonials.map((_, index) =>
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2 rounded-full transition-all duration-250 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                  index === activeIndex ? 'w-8 bg-accent' : 'w-2 bg-muted hover:bg-muted-foreground'}`
                  }
                  aria-label={`Перейти к отзыву ${index + 1}`} />

                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevious}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card text-text-primary transition-all duration-250 hover:bg-muted hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 active:scale-[0.97]"
                  aria-label="Предыдущий отзыв">

                  <Icon name="ChevronLeftIcon" size={20} />
                </button>
                <button
                  onClick={handleNext}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card text-text-primary transition-all duration-250 hover:bg-muted hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 active:scale-[0.97]"
                  aria-label="Следующий отзыв">

                  <Icon name="ChevronRightIcon" size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;